import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { getRideReceipt } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Copy, Loader2 } from 'lucide-react';

const Row = ({ label, value, bold }) => (
  <div className={`flex items-center justify-between py-1.5 text-sm ${bold ? 'font-bold text-base' : ''}`}>
    <span className="text-gray-600">{label}</span>
    <span className={bold ? 'text-black' : 'text-gray-900'}>{value}</span>
  </div>
);

export const RideReceipt = ({ rideId, open, onClose, isArabic }) => {
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !rideId) return;
    setLoading(true);
    setReceipt(null);
    getRideReceipt(rideId)
      .then((res) => setReceipt(res.data))
      .catch(() => toast({ title: isArabic ? 'تعذر تحميل الإيصال' : 'Could not load receipt', variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [open, rideId, isArabic]);

  const syp = (n) => `${(n || 0).toLocaleString()} ${isArabic ? 'ل.س' : 'SYP'}`;

  const copyReceipt = () => {
    if (!receipt) return;
    const text = [
      `Saeed Taxi — ${isArabic ? 'إيصال الرحلة' : 'Ride Receipt'} ${receipt.receiptNo}`,
      `${isArabic ? 'التاريخ' : 'Date'}: ${receipt.date ? new Date(receipt.date).toLocaleString(isArabic ? 'ar-SY' : 'en-US') : '-'}`,
      `${isArabic ? 'من' : 'From'}: ${receipt.pickup}`,
      `${isArabic ? 'إلى' : 'To'}: ${receipt.dropoff}`,
      `${isArabic ? 'المسافة' : 'Distance'}: ${receipt.distanceKm} km`,
      `${isArabic ? 'الإجمالي' : 'Total'}: ${syp(receipt.total)}`,
    ].join('\n');
    navigator.clipboard.writeText(text);
    toast({ title: isArabic ? 'تم نسخ الإيصال' : 'Receipt copied to clipboard' });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="ride-receipt-dialog">
        <DialogHeader>
          <DialogTitle>{isArabic ? 'إيصال الرحلة' : 'Ride Receipt'}</DialogTitle>
        </DialogHeader>
        {loading && (
          <div className="flex justify-center py-8" data-testid="receipt-loading">
            <Loader2 className="h-6 w-6 animate-spin text-green-600" />
          </div>
        )}
        {receipt && (
          <div data-testid="receipt-content">
            <div className="text-center mb-4">
              <p className="font-black text-lg">Saeed Taxi</p>
              <p className="text-xs text-gray-500" data-testid="receipt-number">{receipt.receiptNo}</p>
              <p className="text-xs text-gray-500">
                {receipt.date ? new Date(receipt.date).toLocaleString(isArabic ? 'ar-SY' : 'en-US') : ''}
              </p>
            </div>
            <div className="border-t border-b py-2 mb-2 space-y-1 text-sm">
              <p><span className="text-green-600 font-bold">↑</span> {receipt.pickup}</p>
              <p><span className="text-red-600 font-bold">↓</span> {receipt.dropoff}</p>
            </div>
            <Row label={isArabic ? 'السائق' : 'Driver'} value={receipt.driverName || '-'} />
            <Row label={isArabic ? 'نوع السيارة' : 'Car type'} value={receipt.carType} />
            <Row label={isArabic ? 'المسافة' : 'Distance'} value={`${receipt.distanceKm} km`} />
            <Row label={`${isArabic ? 'الأجرة الأساسية' : 'Base fare'} (${receipt.ratePerKm.toLocaleString()}/km)`} value={syp(receipt.baseAmount)} />
            {receipt.carTypeAmount > 0 && (
              <Row label={isArabic ? 'رسوم فئة السيارة' : 'Car class fee'} value={syp(receipt.carTypeAmount)} />
            )}
            {receipt.surgeAmount > 0 && (
              <Row label={`${isArabic ? 'زيادة الطلب' : 'Surge'} (×${receipt.surge})`} value={syp(receipt.surgeAmount)} />
            )}
            <div className="border-t mt-2 pt-1">
              <Row bold label={isArabic ? 'الإجمالي' : 'Total'} value={syp(receipt.total)} />
            </div>
            <Row label={isArabic ? 'طريقة الدفع' : 'Payment'} value={receipt.paymentMethod || 'cash'} />
            <Button onClick={copyReceipt} className="w-full mt-4 bg-black hover:bg-gray-800" data-testid="copy-receipt-btn">
              <Copy className="h-4 w-4 mr-2" />
              {isArabic ? 'نسخ الإيصال' : 'Copy Receipt'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RideReceipt;
